import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ExternalLink, Shield, Heart } from 'lucide-react';


const begambleawareImg = '/images/begambleaware.png';
const gamblingCommissionImg = '/images/gambling_commission_logo.png';
const gamcareImg = '/images/gamcare.png';
const gamstopImg = '/images/gamstop_logo.png';
const ageRestrictionImg = '/images/18+.png';


const Footer = () => {
  const currentYear = 2025; // Based on the date provided (16.12.2025)

  return (
    <footer className="bg-book-brown text-book-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" data-discover="true" className="flex items-center space-x-2 mb-4" itemScope itemType="https://schema.org/Organization">
              <img src="/images/favicon.webp" alt="Brit Ember Logo" className="h-8 w-8" itemProp="logo" />
              <span className="text-2xl font-bold" itemProp="name">Brit Ember</span>
            </Link>
            <p className="text-book-cream opacity-90 leading-relaxed mb-4" itemProp="description">
              Your trusted gateway to the world's most prestigious gaming experiences.
              Where excellence meets entertainment.
            </p>
            
          </div>

          <div>
            <h3 className="text-book-cream font-bold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" data-discover="true" className="text-book-cream hover:text-accent-red transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/guides" data-discover="true" className="text-book-cream hover:text-accent-red transition-colors">
                  Guides
                </Link>
              </li>
              <li>
                <Link to="/news" data-discover="true" className="text-book-cream hover:text-accent-red transition-colors">
                  News
                </Link>
              </li>
              <li>
                <Link to="/reviews" data-discover="true" className="text-book-cream hover:text-accent-red transition-colors">
                  Reviews
                </Link>
              </li>
              <li>
                <Link to="/slots" data-discover="true" className="text-book-cream hover:text-accent-red transition-colors">
                  Slots
                </Link>
              </li>
            </ul>
          </div>




          <div>
            <h3 className="text-book-cream font-bold mb-4">Legal links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy-policy" data-discover="true" className="text-book-cream hover:text-accent-red transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-conditions" data-discover="true" className="text-book-cream hover:text-accent-red transition-colors">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/cookies-policy" data-discover="true" className="text-book-cream hover:text-accent-red transition-colors">
                  Cookies Policy
                </Link>
              </li>
              <li>
                <Link to="/responsible-gaming" data-discover="true" className="text-book-cream hover:text-accent-red transition-colors">
                  Responsible Gaming
                </Link>
              </li>
            </ul>
          </div>



          <div>
            <h3 className="text-book-cream font-bold mb-4">Responsible Gaming</h3>
            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-8 mb-8">
              <div className="flex items-center">
                <a
                    href="https://www.begambleaware.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                >
                  <img
                      className="h-[60px] w-auto object-contain"
                      alt="BeGambleAware - Responsible Gambling"
                      src={begambleawareImg}
                  />
                </a>
              </div>

              <div className="flex items-center">
                <a
                    href="https://www.gamblingcommission.gov.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                >
                  <img
                      className="h-[60px] w-auto object-contain"
                      alt="UK Gambling Commission"
                      src={gamblingCommissionImg}
                  />
                </a>
              </div>

              <div className="flex items-center">
                <a
                    href="https://www.gamcare.org.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                >
                  <img
                      className="h-[60px] w-auto object-contain"
                      alt="GamCare - Gambling Support"
                      src={gamcareImg}
                  />
                </a>
              </div>

              <div className="flex items-center">
                <a
                    href="https://www.gamstop.co.uk/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                >
                  <img
                      className="h-[60px] w-auto object-contain"
                      alt="Gamstop - Self-Exclusion"
                      src={gamstopImg}
                  />
                </a>
              </div>

              <div className="flex items-center">
                <img
                    className="h-[60px] w-auto object-contain"
                    alt="18+ Age Restriction"
                    src={ageRestrictionImg}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer Section */}
        <div className="border-t border-book-brown-light py-8">
          <div className="bg-book-beige rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-3">
              <Shield className="h-6 w-6 text-book-brown flex-shrink-0 mt-1" />
              <div>
                <h4 className="text-book-brown font-semibold mb-2">Our Commitment to Transparency</h4>
                <p className="text-book-brown-dark text-sm leading-relaxed">
                  Brit Ember operates as an independent gaming guide, dedicated to connecting you with exceptional entertainment experiences.
                  Some links featured throughout our site are affiliate partnerships, which means we may receive a commission when you explore these premium platforms.
                  Rest assured, this never compromises our genuine recommendations, honest reviews, or editorial integrity.
                  We only champion operators that truly deserve your trust and deliver outstanding value.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-red-50 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2 text-red-800">
              <Heart className="h-5 w-5" />
              <div>
                <strong className="font-semibold">Responsible Gaming:</strong>
                <span className="ml-2 text-sm">
                  Gaming should be an enjoyable pursuit for adults 18+. Always play responsibly.
                  Visit <a href="https://www.begambleaware.org/" target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">BeGambleAware.org</a> for support.
                  All promotions are subject to individual operator terms and conditions.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-book-brown-light py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-book-cream opacity-80 text-sm">
              © {currentYear} Brit Ember. All rights reserved. | Domain: britember.com
            </div>
            <div className="flex space-x-6 text-sm">
              <a href="#terms" className="text-book-cream opacity-80 hover:text-book-gold transition-colors duration-200">
                Terms & Conditions
              </a>
              <a href="#privacy" className="text-book-cream opacity-80 hover:text-book-gold transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="#cookies" className="text-book-cream opacity-80 hover:text-book-gold transition-colors duration-200">
                Cookie Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
